import { zodResolver } from '@hookform/resolvers/zod';
import { loginRequestSchema, loginResponseSchema, twoFactorChallengeSchema } from '@hearth/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { Loader2 } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import type { z } from 'zod';
import { FormField } from '@/components/form-field';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { apiRequest } from '@/lib/api';
import { applyServerErrors } from '@/lib/forms';
import { sessionQuery } from '@/lib/session';

/** Only same-site relative paths, so ?redirect= can't send users to another site. */
const safeRedirect = (value: unknown) =>
  typeof value === 'string' && value.startsWith('/') && !value.startsWith('//') ? value : undefined;

export const Route = createFileRoute('/_guest/login')({
  validateSearch: (search: Record<string, unknown>): { redirect?: string } => ({
    redirect: safeRedirect(search.redirect),
  }),
  component: LoginPage,
});

function LoginPage() {
  const { data: session } = useQuery(sessionQuery);
  const [step, setStep] = useState<'password' | 'two-factor'>(
    session && !session.authenticated && session.twoFactorPending ? 'two-factor' : 'password',
  );
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { redirect } = Route.useSearch();

  const finish = async () => {
    await queryClient.resetQueries({ queryKey: sessionQuery.queryKey });
    await navigate({ to: redirect ?? '/' });
  };

  return step === 'password' ? (
    <PasswordStep
      onTwoFactor={() => setStep('two-factor')}
      onDone={finish}
      registration={Boolean(session?.panel.registrationEnabled)}
    />
  ) : (
    <TwoFactorStep onDone={finish} onBack={() => setStep('password')} />
  );
}

function PasswordStep({
  onTwoFactor,
  onDone,
  registration,
}: {
  onTwoFactor: () => void;
  onDone: () => Promise<void>;
  registration: boolean;
}) {
  const form = useForm<z.input<typeof loginRequestSchema>>({
    resolver: zodResolver(loginRequestSchema),
  });
  const login = useMutation({
    mutationFn: (body: z.input<typeof loginRequestSchema>) =>
      apiRequest('/api/auth/login', { method: 'POST', body, schema: loginResponseSchema }),
    onSuccess: (res) => (res.status === 'two_factor_required' ? onTwoFactor() : onDone()),
    onError: (err) => {
      applyServerErrors(err, form.setError);
      form.resetField('password');
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Sign in</CardTitle>
        <CardDescription>Use your email address or username.</CardDescription>
      </CardHeader>
      <form onSubmit={form.handleSubmit((v) => login.mutate(v))} noValidate>
        <CardContent className="grid gap-4">
          <FormField label="Email or username" error={form.formState.errors.login?.message}>
            <Input autoComplete="username" autoFocus {...form.register('login')} />
          </FormField>
          <FormField label="Password" error={form.formState.errors.password?.message}>
            <Input type="password" autoComplete="current-password" {...form.register('password')} />
          </FormField>
          <Link
            to="/forgot-password"
            className="text-muted-foreground hover:text-foreground -mt-2 justify-self-end text-xs"
          >
            Forgot password?
          </Link>
        </CardContent>
        <CardFooter className="mt-6 flex-col gap-3">
          <Button type="submit" className="w-full" disabled={login.isPending}>
            {login.isPending && <Loader2 className="animate-spin" />} Sign in
          </Button>
          {registration && (
            <p className="text-muted-foreground text-sm">
              New here?{' '}
              <Link to="/register" className="text-foreground underline-offset-4 hover:underline">
                Create an account
              </Link>
            </p>
          )}
        </CardFooter>
      </form>
    </Card>
  );
}

function TwoFactorStep({ onDone, onBack }: { onDone: () => Promise<void>; onBack: () => void }) {
  const [useRecovery, setUseRecovery] = useState(false);
  const form = useForm<z.input<typeof twoFactorChallengeSchema>>({
    resolver: zodResolver(twoFactorChallengeSchema),
  });
  const verify = useMutation({
    mutationFn: (body: z.input<typeof twoFactorChallengeSchema>) =>
      apiRequest('/api/auth/login/two-factor', {
        method: 'POST',
        body,
        schema: loginResponseSchema,
      }),
    onSuccess: () => onDone(),
    onError: (err) => {
      applyServerErrors(err, form.setError);
      form.resetField('code');
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Two-factor authentication</CardTitle>
        <CardDescription>
          {useRecovery
            ? 'Enter one of your recovery codes.'
            : 'Enter the 6-digit code from your authenticator app.'}
        </CardDescription>
      </CardHeader>
      <form onSubmit={form.handleSubmit((v) => verify.mutate(v))} noValidate>
        <CardContent className="grid gap-4">
          <FormField
            label={useRecovery ? 'Recovery code' : 'Authentication code'}
            error={form.formState.errors.code?.message}
          >
            <Input
              autoFocus
              autoComplete="one-time-code"
              inputMode={useRecovery ? 'text' : 'numeric'}
              placeholder={useRecovery ? 'XXXXX-XXXXX' : '123456'}
              {...form.register('code')}
            />
          </FormField>
        </CardContent>
        <CardFooter className="mt-6 flex-col gap-3">
          <Button type="submit" className="w-full" disabled={verify.isPending}>
            {verify.isPending && <Loader2 className="animate-spin" />} Verify
          </Button>
          <div className="flex w-full justify-between text-xs">
            <button
              type="button"
              className="text-muted-foreground hover:text-foreground"
              onClick={onBack}
            >
              Back
            </button>
            <button
              type="button"
              className="text-muted-foreground hover:text-foreground"
              onClick={() => {
                setUseRecovery((v) => !v);
                form.reset();
              }}
            >
              {useRecovery ? 'Use authenticator code' : 'Use a recovery code'}
            </button>
          </div>
        </CardFooter>
      </form>
    </Card>
  );
}
